import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { isEmpty } from "lodash";
import {
  useOnboardingWorkflowById,
  useOnboardingStages,
  useRemoveOnboardingWorkflowFromStage,
  useCreateOnboardingWorkflowItem,
  useUpdateOnboardingWorkflowItem,
  useDeleteOnboardingWorkflowItem,
  useFetchOnboardingWorkflowItem,
  useUpdateOnboardingWorkflowItemsOrder
} from "@/pages/settings/components/contractsSettings/hooks";
import GuardrailsSection from "@/pages/settings/components/dealsSettings/StageStudio/workflow/Guardrails";
import { QuestionnaireType } from "@/components/controls/Questionnaires/Questionnaire";
import { QuestionnaireBaseOutput } from "@/pages/deals/types";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import Skeleton from "@/components/ui/skeleton";
import Banner from "@/components/ui/banner";
import IconContainer from "@/components/ui/icon-container";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import AddActionButton from "@/components/ui/add-action-button";
import { Delete, Workflow } from "@dreamhub/designsystem/icons";
import Section from "@/pages/settings/components/sections/Section";
import { SettingItemLayoutProps } from "@/pages/settings/components/settingsItems/SettingItemLayout";
import SettingsNavigation from "@/pages/settings/components/SettingsNavigation";
import { FieldManagementTable } from "@/pages/settings/components/dealsSettings/FieldManagement/FieldManagementTable";
import { APIFieldItem } from "@/pages/settings/types";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { AddWorkflowFieldDrawer } from "@/pages/settings/components/dealsSettings/StageStudio/workflow/AddWorkflowFieldDrawer";
import { EditWorkflowFieldDrawer } from "@/pages/settings/components/dealsSettings/StageStudio/workflow/EditWorkflowFieldDrawer";
import { AutofillTargetContext } from "@/pages/settings/components/dealsSettings/StageStudio/workflow/hooks/autofillTarget";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import EditOnboardingWorkflowDetailsDrawer from "./EditOnboardingWorkflowDetailsDrawer";
import { ContractsWorkflowType } from "../addWorkflow/types";
import OnboardingRemoveFromStageModal from "./OnboardingRemoveFromStageModal";
import {
  isPublishedOnboardingItem,
  mapFieldDataToCreateOnboardingItem,
  mapFieldDataToPublishOnboardingDraft,
  mapFieldDataToUpdateOnboardingItem,
  mapOnboardingWorkflowType,
  transformOnboardingItemsToFields,
  WorkflowFieldFormData
} from "./helpers";
import { CONTRACT_WORKFLOW_ITEM_AUTOFILL_TARGET } from "./autofillTarget";
import { getOnboardingStageColor } from "../colors";

const TRANSLATION_PREFIX =
  "settings.contracts.onboardingStudio.workflowDetail";
const STUDIO_PATH = "/settings/contracts?tab=onboardingStudio";

const WORKFLOW_QUERY_KEY = "settings/contracts/stages/workflows/workflowById";

const OnboardingWorkflowDetailedView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { stageId, workflowId } = useParams<{
    stageId: string;
    workflowId: string;
  }>();

  const { data: workflow, isLoading } = useOnboardingWorkflowById(
    workflowId || "",
    !isEmpty(workflowId)
  );

  const { data: stagesData } = useOnboardingStages();
  const stages = useMemo(() => stagesData?.stages || [], [stagesData]);

  const removeWorkflowFromStage = useRemoveOnboardingWorkflowFromStage();
  const createItemMutation = useCreateOnboardingWorkflowItem();
  const updateItemMutation = useUpdateOnboardingWorkflowItem();
  const deleteItemMutation = useDeleteOnboardingWorkflowItem();
  const fetchItemMutation = useFetchOnboardingWorkflowItem();
  const reorderItemsMutation = useUpdateOnboardingWorkflowItemsOrder();
  const { enabled: isAutofillEnabled } = useFeatureFlag(
    FeatureFlags.CRM_10893_CONTRACT_ONBOARDING_AUTOFILL
  );

  const [isEditDetailsDrawerOpen, setIsEditDetailsDrawerOpen] = useState(false);
  const [isAddFieldDrawerOpen, setIsAddFieldDrawerOpen] = useState(false);
  const [isEditFieldDrawerOpen, setIsEditFieldDrawerOpen] = useState(false);
  const [selectedFieldForEdit, setSelectedFieldForEdit] =
    useState<APIFieldItem | null>(null);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isRemoveConfirmed, setIsRemoveConfirmed] = useState(false);

  const items = useMemo(
    () => (workflow?.items || []).filter(isPublishedOnboardingItem),
    [workflow]
  );
  // Reads now hide drafts, so this is normally empty; the reorder endpoint accepts drafts either way.
  const draftItemIds = useMemo(
    () =>
      (workflow?.items || [])
        .filter((item) => !isPublishedOnboardingItem(item))
        .map((item) => item.id),
    [workflow]
  );
  const workflowType = mapOnboardingWorkflowType(workflow?.workflowType);
  const workflowTypeLabelKey =
    workflow?.workflowType === ContractsWorkflowType.CHECKLIST
      ? "checklist"
      : workflowType;

  const [localItems, setLocalItems] = useState(items);

  useEffect(() => {
    setLocalItems(items);
  }, [items]);

  const fields = useMemo(
    () => transformOnboardingItemsToFields(localItems),
    [localItems]
  );

  const stage = useMemo(
    () => stages.find((s) => s.id === workflow?.contractsOnboardingStageId),
    [stages, workflow?.contractsOnboardingStageId]
  );

  const guardrailsQuestionnaire = useMemo(
    () =>
      ({
        questions: localItems.map((item) => ({
          id: item.id,
          isMandatory: item.isGuardrails
        }))
      }) as unknown as QuestionnaireBaseOutput,
    [localItems]
  );

  const refetchWorkflow = () =>
    queryClient.invalidateQueries({ queryKey: [WORKFLOW_QUERY_KEY] });

  const handleSaveField = async (fieldData: WorkflowFieldFormData) => {
    if (!workflowId) return;
    const { autofillQuestionId, dreamerFlow } = fieldData;
    try {
      if (autofillQuestionId && dreamerFlow) {
        // The autofill section already created the item as a draft; publish it.
        await updateItemMutation.mutateAsync({
          pathParams: { itemId: autofillQuestionId },
          data: mapFieldDataToPublishOnboardingDraft(
            { ...fieldData, dreamerFlow },
            workflowId
          )
        });
      } else {
        await createItemMutation.mutateAsync({
          data: mapFieldDataToCreateOnboardingItem(fieldData, workflowId)
        });
      }
    } catch (error) {
      console.error("Failed to add workflow field:", error);
      // A draft that failed to publish is hidden from the list, so nothing else would delete it.
      if (autofillQuestionId && dreamerFlow) {
        // The publish may have landed with its response lost; delete only a confirmed draft.
        const draft = await fetchItemMutation
          .mutateAsync({ pathParams: { itemId: autofillQuestionId } })
          .catch(() => undefined);
        if (draft && isPublishedOnboardingItem(draft)) {
          await refetchWorkflow();
          toast.success(t(`${TRANSLATION_PREFIX}.fields.addedSuccessfully`), {
            duration: 5000
          });
          return;
        }
        if (draft) {
          try {
            await deleteItemMutation.mutateAsync({
              pathParams: { itemId: autofillQuestionId }
            });
          } catch (deleteError) {
            console.error("Failed to delete unpublished draft:", deleteError);
          }
        }
      }
      toast.error(t(`${TRANSLATION_PREFIX}.fields.addFailed`));
      return;
    }
    await refetchWorkflow();
    toast.success(t(`${TRANSLATION_PREFIX}.fields.addedSuccessfully`), {
      duration: 5000
    });
  };

  const handleEditField = (field: APIFieldItem) => {
    setSelectedFieldForEdit(field);
    setIsEditFieldDrawerOpen(true);
  };

  const handleSaveEditedField = async (fieldData?: WorkflowFieldFormData) => {
    if (!fieldData?.id) {
      await refetchWorkflow();
      return;
    }
    await updateItemMutation.mutateAsync({
      pathParams: { itemId: fieldData.id },
      data: mapFieldDataToUpdateOnboardingItem(fieldData, isAutofillEnabled)
    });
    await refetchWorkflow();
  };

  const handleDeleteField = async (fieldId: string) => {
    if (!fieldId) return;
    await deleteItemMutation.mutateAsync({
      pathParams: { itemId: fieldId }
    });
    await refetchWorkflow();
  };

  const handleItemsReorder = async (result: {
    source: { index: number };
    destination?: { index: number } | null;
  }) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const newItems = [...localItems];
    const [moved] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, moved);
    setLocalItems(newItems);

    try {
      await reorderItemsMutation.mutateAsync({
        data: [...newItems.map((item) => item.id), ...draftItemIds]
      });
    } catch (error) {
      console.error("Failed to reorder workflow items:", error);
      setLocalItems(items);
    }
  };

  const handleCloseEditFieldDrawer = () => {
    setIsEditFieldDrawerOpen(false);
    setSelectedFieldForEdit(null);
  };

  const handleRemoveFromStage = async () => {
    if (!workflow?.id) return;
    await removeWorkflowFromStage.mutateAsync({
      pathParams: { workflowId: workflow.id }
    });
    setIsRemoveModalOpen(false);
    setIsEditDetailsDrawerOpen(false);
    toast.success(t(`${TRANSLATION_PREFIX}.removeFromStageModal.successToast`), {
      duration: 5000
    });
    navigate(stageId ? `/settings/contracts/stages/${stageId}` : STUDIO_PATH);
  };

  return (
    <>
      <div className="flex w-full items-center justify-center">
        <div className="w-full max-w-[800px] flex flex-col gap-12 pt-6 pb-12">
          <div className="flex flex-col gap-10">
            <SettingsNavigation
              isLoading={isLoading}
              items={[
                {
                  label: t(`${TRANSLATION_PREFIX}.navigation.contractsSettings`),
                  to: "/settings/contracts"
                },
                {
                  label: t(`${TRANSLATION_PREFIX}.navigation.onboardingStudio`),
                  to: STUDIO_PATH
                },
                {
                  label: t(`${TRANSLATION_PREFIX}.navigation.stageName`, {
                    stage: stage?.name
                  }),
                  to: stageId
                    ? `/settings/contracts/stages/${stageId}`
                    : STUDIO_PATH
                },
                {
                  label: workflow?.name || "",
                  isActive: true
                }
              ]}
            />
            {isLoading ? (
              <Skeleton className="h-7 w-[15rem]" />
            ) : (
              <div className="flex flex-row justify-between">
                <div className="flex gap-4 items-center">
                  <LogoIconContainer
                    index={-1}
                    icon={<Workflow />}
                    iconSize="md"
                  />
                  <Text variant="title3H1">{workflow?.name || ""}</Text>
                </div>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsRemoveConfirmed(false);
                    setIsRemoveModalOpen(true);
                  }}
                >
                  <IconContainer>
                    <Delete />
                  </IconContainer>
                  {t(`${TRANSLATION_PREFIX}.removeFromStage`)}
                </Button>
              </div>
            )}
          </div>

          {!isLoading && isEmpty(items) ? (
            <Banner
              variant="general"
              title={t(`${TRANSLATION_PREFIX}.emptyWorkflowBanner.message`)}
              buttonText={t(`${TRANSLATION_PREFIX}.emptyWorkflowBanner.addField`)}
              onButtonClick={() => setIsAddFieldDrawerOpen(true)}
              showCloseButton={false}
            />
          ) : null}

          <Section
            isLoading={isLoading}
            title={t(`${TRANSLATION_PREFIX}.sections.workflowDetails`)}
            action={
              <AddActionButton
                hideIcon
                label={t("edit")}
                onClick={() => setIsEditDetailsDrawerOpen(true)}
              />
            }
            items={
              [
                {
                  title: t(`${TRANSLATION_PREFIX}.fields.workflowType`),
                  textValue: t(
                    `settings.deals.workflow.workflowDetailedView.workflowTypes.${workflowTypeLabelKey}`
                  ),
                  titleClassName: "text-secondary"
                },
                {
                  title: t(`${TRANSLATION_PREFIX}.fields.workflowName`),
                  textValue: workflow?.name,
                  titleClassName: "text-secondary"
                },
                {
                  title: t(`${TRANSLATION_PREFIX}.fields.workflowDescription`),
                  textValue: workflow?.description || "",
                  titleClassName: "text-secondary"
                },
                {
                  title: t(`${TRANSLATION_PREFIX}.fields.stage`),
                  textValue: stage?.name ? (
                    <div className="flex items-center gap-3">
                      <div
                        className="min-w-[6px] w-[6px] h-6 rounded-xl"
                        style={{
                          backgroundColor: getOnboardingStageColor(
                            stage?.stageColor
                          )
                        }}
                      />
                      <Text variant="body">{stage.name}</Text>
                    </div>
                  ) : (
                    "-"
                  ),
                  titleClassName: "text-secondary"
                }
              ] as SettingItemLayoutProps[]
            }
          />

          <Section
            isLoading={isLoading}
            title={t(`${TRANSLATION_PREFIX}.sections.workflowFields`)}
            action={
              <AddActionButton
                onClick={() => setIsAddFieldDrawerOpen(true)}
                label={t(`${TRANSLATION_PREFIX}.actions.addField`)}
              />
            }
          >
            <FieldManagementTable
              entityType={EntityType.CONTRACT}
              fields={fields}
              onDragEnd={handleItemsReorder}
              onEditField={handleEditField}
              onDeleteField={handleDeleteField}
              showWorkflowColumn={true}
              workflowType={workflowType}
              onAddField={() => setIsAddFieldDrawerOpen(true)}
              onRowClick={handleEditField}
            />
          </Section>

          <Section
            title={t(`${TRANSLATION_PREFIX}.guardrails.title`)}
            subTitle={t(`${TRANSLATION_PREFIX}.guardrails.subTitle`)}
          >
            <GuardrailsSection
              questionCount={localItems.length}
              questionnaire={guardrailsQuestionnaire}
              workflowType={QuestionnaireType.GENERAL}
            />
          </Section>
        </div>
      </div>

      <EditOnboardingWorkflowDetailsDrawer
        isOpen={isEditDetailsDrawerOpen}
        onOpenChange={setIsEditDetailsDrawerOpen}
        workflow={workflow}
        stages={stages}
        onSave={refetchWorkflow}
        onRemoveFromStage={() => {
          setIsRemoveConfirmed(false);
          setIsRemoveModalOpen(true);
        }}
      />

      <AutofillTargetContext.Provider
        value={CONTRACT_WORKFLOW_ITEM_AUTOFILL_TARGET}
      >
        <AddWorkflowFieldDrawer
          isOpen={isAddFieldDrawerOpen}
          onOpenChange={setIsAddFieldDrawerOpen}
          workflowType={workflowType}
          questionnaireId={workflowId || ""}
          enableAutofill={isAutofillEnabled}
          forceChecklistItem={
            workflow?.workflowType === ContractsWorkflowType.CHECKLIST
          }
          onSave={handleSaveField}
        />

        <EditWorkflowFieldDrawer
          isOpen={isEditFieldDrawerOpen}
          onOpenChange={handleCloseEditFieldDrawer}
          fieldData={selectedFieldForEdit}
          workflowType={workflowType}
          questionnaireId={workflowId || ""}
          enableAutofill={isAutofillEnabled}
          onSave={handleSaveEditedField}
          onSaveGeneralField={handleSaveEditedField}
          onDelete={handleDeleteField}
          onGeneralSave={refetchWorkflow}
        />
      </AutofillTargetContext.Provider>

      <OnboardingRemoveFromStageModal
        isOpen={isRemoveModalOpen}
        setIsOpen={setIsRemoveModalOpen}
        isConfirmed={isRemoveConfirmed}
        setIsConfirmed={setIsRemoveConfirmed}
        onConfirm={handleRemoveFromStage}
      />
    </>
  );
};

export default OnboardingWorkflowDetailedView;
